import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import classNames from "classnames";
import { Formik, FormikProps } from "formik";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Modal from "react-modal";
import NumberFormat, { NumberFormatValues } from "react-number-format";

import IconCross from "../../../public/svg/icon-cross.svg";
import { ThemeContext } from "../../contexts/ThemeContext";
import usePhotoUpload, {
  SEASON_PASS_UPLOAD_IMAGE_URI,
} from "../../hooks/usePhotoUpload";
import {
  DATE_MASK_BY_LOCALE,
  DATE_PLACEHOLDER_BY_LOCALE,
} from "../../lib/i18n";
import { isUnderAge } from "../../lib/yup";
import {
  SeasonCardApplicationFields,
  SeasonCardApplicationSchema,
} from "../../schemas/SeasonCardApplication.schema";
import { readBlobAsDataURL } from "../../utils/image";
import LocaleLink from "../LocaleLink";
import ErrorContainer from "../account/ErrorContainer";
import FieldErrors from "../account/FieldErrors";

import SeasonPassPhotoUpload from "./SeasonPassPhotoUpload";
import { SeasonCardApplicationEditData } from "./SeasonPassProductActions";
import css from "./SeasonPassProductModal.module.scss";

export const TEST_ID_SEASONPASS_APPLICATION_FORM =
  "seasonpass-application-form";

export const TEST_ID_FIRST_NAME = "firstName-input";
export const TEST_ID_LAST_NAME = "lastName-input";
export const TEST_ID_DATE_OF_BIRTH = "dateOfBirth-input";
export const TEST_ID_GUARDIAN_FIRST_NAME = "guardianFirstName-input";
export const TEST_ID_GUARDIAN_LAST_NAME = "guardianLastName-input";
export const TEST_ID_ADDRESS = "address-input";
export const TEST_ID_POSTAL_CODE = "postalCode-input";
export const TEST_ID_CITY = "city-input";
export const TEST_ID_EMAIL = "email-input";
export const TEST_ID_PHONE = "phone-input";
export const TEST_ID_TERMS_ACCEPTED = "termsAccepted-checkbox";

export const TEST_ID_CLOSE_BUTTON = "close-button";
export const TEST_ID_CANCEL_BUTTON = "cancel-button";
export const TEST_ID_SUBMIT_BUTTON = "submit-button";

const DATE_INVALID = "invalid";
const DATE_FORMAT = "dd.MM.yyyy";
const OVERLAY_CLASSNAME = "confirm-overlay";

interface SeasonPassProductModalProps {
  open: boolean;
  submit: (
    fields: SeasonCardApplicationFields,
    photoId: string,
    cartLineId?: string,
  ) => void;
  onClose: () => void;
  initialFields: SeasonCardApplicationFields;
  initialPhotoId?: string;
  editData?: SeasonCardApplicationEditData;
}

function onAfterOpen() {
  const [overlay] = document.getElementsByClassName(OVERLAY_CLASSNAME);
  if (overlay) {
    disableBodyScroll(overlay, {
      reserveScrollBarGap: true,
    });
  }
}

const SeasonPassProductModal: React.FC<SeasonPassProductModalProps> = ({
  open,
  submit,
  onClose,
  editData,
  initialFields,
  initialPhotoId,
}) => {
  const { styles } = useContext(ThemeContext);
  const { formatMessage } = useIntl();

  const [isUnder16, setIsUnder16] = useState(
    isUnderAge(initialFields.dateOfBirth),
  );
  const validateAge = useCallback(
    (values: SeasonCardApplicationFields): void => {
      setIsUnder16(values.dateOfBirth ? isUnderAge(values.dateOfBirth) : false);
    },
    [],
  );
  const {
    photo,
    setPhoto,
    isLoading,
    reset,
    storePhoto,
    uploadPhoto,
    hasChanged,
  } = usePhotoUpload();

  useEffect(() => {
    if (editData) {
      if (isUnderAge(editData.fields.dateOfBirth)) {
        setIsUnder16(true);
      }
      if (editData.photoId) {
        setPhoto(`${SEASON_PASS_UPLOAD_IMAGE_URI}/${editData.photoId}`);
      }
    }
  }, [editData, setPhoto]);

  useEffect(() => {
    if (initialPhotoId) {
      setPhoto(`${SEASON_PASS_UPLOAD_IMAGE_URI}/${initialPhotoId}`);
    }
  }, [initialPhotoId, setPhoto]);

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const handleClose = useCallback(() => {
    setSubmitError(undefined);
    reset();
    onClose();
  }, [reset, onClose]);

  const cartLineId = editData?.cartLineId;
  const photoId = editData?.photoId ?? initialPhotoId;

  const handleSubmit = useCallback(
    async (fields: SeasonCardApplicationFields) => {
      try {
        if (!isUnder16) {
          fields.guardianFirstName = undefined;
          fields.guardianLastName = undefined;
        }

        if (fields.ticketId === "") {
          fields.ticketId = undefined;
        }

        let currPhoto = photoId;
        if (photo && hasChanged) {
          const result = await uploadPhoto(photo);
          if (result.id) {
            currPhoto = result.id;
          }
        }
        setIsUnder16(false);
        if (currPhoto) {
          submit(fields, currPhoto, cartLineId);
          reset();
          onClose();
        }
      } catch (e) {
        if (e === "error.imageUploadFailed") {
          setSubmitError(e);
        } else {
          setSubmitError("error.seasonPassSubmitFailed");
        }
      }
    },
    [
      isUnder16,
      photoId,
      cartLineId,
      photo,
      hasChanged,
      uploadPhoto,
      submit,
      reset,
      onClose,
    ],
  );

  const clearError = useCallback(() => {
    setSubmitError(undefined);
  }, []);

  return (
    <Modal
      isOpen={open}
      onRequestClose={handleClose}
      onAfterOpen={onAfterOpen}
      onAfterClose={clearAllBodyScrollLocks}
      className={classNames(
        css.seasonPassProductModal,
        "outline-0",
        "br-1",
        "bg-white",
        "pa4",
        "modal-content",
      )}
      overlayClassName={OVERLAY_CLASSNAME}
    >
      <span className="black">
        <button
          className="absolute top-1 right-1 svg-icon icon-wrapper-small bg-transparent ba bw1 br-100 b--inherit stroke-current-color hover-green flex items-center justify-center pointer"
          aria-label={formatMessage({ id: "menu.close" })}
          onClick={handleClose}
          data-testid={TEST_ID_CLOSE_BUTTON}
        >
          <IconCross />
        </button>
      </span>
      <div className="ph6-ns ph4-m ph5-l pv5">
        <h3 className={`${styles.heading2} tc mt1 mb4`}>
          <FormattedMessage id="seasonpass.applicationTitle" />
        </h3>
        <Formik
          initialValues={editData?.fields ?? initialFields}
          validationSchema={SeasonCardApplicationSchema}
          validate={validateAge}
          onSubmit={handleSubmit}
        >
          {(formik): JSX.Element => (
            <SeasonPassProductModalForm
              {...formik}
              isLoading={isLoading}
              isUnder16={isUnder16}
              photo={photo}
              isEditing={!!editData?.cartLineId}
              storePhoto={storePhoto}
              submitError={submitError}
              clearError={clearError}
              onClose={handleClose}
            />
          )}
        </Formik>
      </div>
    </Modal>
  );
};

interface OtherProps {
  isEditing: boolean;
  onClose: () => void;
  isLoading: boolean;
  isUnder16: boolean;
  photo?: string;
  storePhoto: (dataURL: string, minSize: number) => Promise<void>;
  submitError?: string;
  clearError: () => void;
}

const MIN_PHOTO_SIZE_PIXELS = 200;

type SeasonPassProductModalFormProps =
  FormikProps<SeasonCardApplicationFields> & OtherProps;

const SeasonPassProductModalForm: React.FC<SeasonPassProductModalFormProps> = ({
  initialValues,
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isLoading,
  setFieldValue,
  isUnder16,
  isEditing,
  photo,
  storePhoto,
  submitError,
  clearError,
  onClose,
}) => {
  const { styles } = useContext(ThemeContext);
  const { formatMessage, locale } = useIntl();
  const [photoError, setPhotoError] = useState<string | undefined>(undefined);

  const handlePhotoChange = useCallback(
    async (ev: React.ChangeEvent<HTMLInputElement>) => {
      clearError();
      setPhotoError(undefined);
      if (ev.target.files?.length) {
        const blob = ev.target.files[0] as Blob;
        try {
          const dataURL = await readBlobAsDataURL(blob);
          await storePhoto(dataURL, MIN_PHOTO_SIZE_PIXELS);
        } catch (error) {
          if (typeof error === "string") {
            if (error === "error.imageTooSmall") {
              setPhotoError(
                formatMessage({ id: error }, { size: MIN_PHOTO_SIZE_PIXELS }),
              );
            } else {
              setPhotoError(formatMessage({ id: error }));
            }
          }
        }
      }
      handleChange(ev);
    },
    [clearError, handleChange, storePhoto, formatMessage],
  );

  const submit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Formik validation happens after handleSubmit, so any extra errors
      // not handled by Formik need to be added before calling handleSubmit.
      if (!photo) {
        setPhotoError(formatMessage({ id: "error.imageMissing" }));
      } else {
        setPhotoError(undefined);
      }
      handleSubmit(e);
    },
    [photo, formatMessage, handleSubmit],
  );

  const [rawDateField, setRawDateField] = useState(
    DateTime.fromISO(initialValues.dateOfBirth)?.toFormat(DATE_FORMAT) || "",
  );

  const onDateChange = useCallback(
    (values: NumberFormatValues) => {
      if (values.formattedValue === "") {
        setFieldValue("dateOfBirth", undefined);
      } else {
        const value = DateTime.fromFormat(
          values.formattedValue,
          DATE_FORMAT,
        ).startOf("day");
        setFieldValue("dateOfBirth", value.toISODate() || DATE_INVALID);
      }
    },
    [setFieldValue],
  );

  const handleDateChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setRawDateField(ev.target.value);
    },
    [],
  );

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = useCallback(
    event => {
      if (event.target.scrollIntoView) {
        event.target.scrollIntoView({ block: "center" });
      }
    },
    [],
  );

  const commonInputProps = {
    className: classNames(styles.input, "db mt3 w-100 pa0 br0"),
    onBlur: handleBlur,
    onFocus: handleFocus,
  };

  return (
    <form
      className="flex flex-column"
      onSubmit={submit}
      data-testid={TEST_ID_SEASONPASS_APPLICATION_FORM}
    >
      <div className="flex flex-wrap justify-between mt3 mv3-ns">
        <div className="w-100 w-50-ns pr2-ns mv3 mv0-ns">
          <label className={`${styles.heading3} ttn`}>
            <FormattedMessage id="forms.seasonpass.holder.firstName" />
            <input
              {...commonInputProps}
              type="text"
              name="firstName"
              autoComplete="given-name"
              placeholder={formatMessage({
                id: "forms.seasonpass.holder.firstName",
              })}
              onChange={handleChange}
              value={values.firstName}
              data-testid={TEST_ID_FIRST_NAME}
            />
            <FieldErrors touched={touched.firstName} value={errors.firstName} />
          </label>
        </div>
        <div className="w-100 w-50-ns pl2-ns mv3 mv0-ns">
          <label className={`${styles.heading3} ttn`}>
            <FormattedMessage id="forms.seasonpass.holder.lastName" />
            <input
              {...commonInputProps}
              type="text"
              name="lastName"
              autoComplete="family-name"
              placeholder={formatMessage({
                id: "forms.seasonpass.holder.lastName",
              })}
              onChange={handleChange}
              value={values.lastName}
              data-testid={TEST_ID_LAST_NAME}
            />
            <FieldErrors touched={touched.lastName} value={errors.lastName} />
          </label>
        </div>
      </div>
      <label className={`${styles.heading3} ttn mv3`}>
        <FormattedMessage id="forms.seasonpass.holder.dateOfBirth" />
        <NumberFormat
          {...commonInputProps}
          format="##.##.####"
          mask={DATE_MASK_BY_LOCALE[locale]}
          type="text"
          name="dateOfBirth"
          autoComplete="bday"
          placeholder={DATE_PLACEHOLDER_BY_LOCALE[locale]}
          onValueChange={onDateChange}
          onChange={handleDateChange}
          value={rawDateField}
          data-testid={TEST_ID_DATE_OF_BIRTH}
        />
        <FieldErrors touched={touched.dateOfBirth} value={errors.dateOfBirth} />
      </label>
      <h2 className={`${styles.heading3} mb1 mt3`}>
        <FormattedMessage
          id={
            isUnder16
              ? "seasonpass.contactTitleGuardian"
              : "seasonpass.contactTitleHolder"
          }
        />
      </h2>
      {isUnder16 && (
        <>
          <p className={`${styles.paragraph3} red mb0`}>
            <FormattedMessage id="seasonpass.needsGuardianContact" />
          </p>
          <div className="flex flex-wrap justify-between mt3 mv3-ns">
            <div className="w-100 w-50-ns pr2-ns mv3 mv0-ns">
              <label className={`${styles.heading3} ttn`}>
                <FormattedMessage id="forms.seasonpass.holder.guardianFirstName" />
                <input
                  {...commonInputProps}
                  type="text"
                  name="guardianFirstName"
                  autoComplete="given-name"
                  placeholder={formatMessage({
                    id: "forms.seasonpass.holder.guardianFirstName",
                  })}
                  onChange={handleChange}
                  value={values.guardianFirstName}
                  data-testid={TEST_ID_GUARDIAN_FIRST_NAME}
                />
                <FieldErrors
                  touched={touched.guardianFirstName}
                  value={errors.guardianFirstName}
                />
              </label>
            </div>
            <div className="w-100 w-50-ns pl2-ns mv3 mv0-ns">
              <label className={`${styles.heading3} ttn`}>
                <FormattedMessage id="forms.seasonpass.holder.guardianLastName" />
                <input
                  {...commonInputProps}
                  type="text"
                  name="guardianLastName"
                  autoComplete="family-name"
                  placeholder={formatMessage({
                    id: "forms.seasonpass.holder.guardianLastName",
                  })}
                  onChange={handleChange}
                  value={values.guardianLastName}
                  data-testid={TEST_ID_GUARDIAN_LAST_NAME}
                />
                <FieldErrors
                  touched={touched.guardianLastName}
                  value={errors.guardianLastName}
                />
              </label>
            </div>
          </div>
        </>
      )}
      <label className={`${styles.heading3} ttn mv3`}>
        <FormattedMessage id="forms.address.address1" />
        <input
          {...commonInputProps}
          type="text"
          name="address"
          autoComplete="address-line1"
          placeholder={formatMessage({ id: "forms.address.address1" })}
          onChange={handleChange}
          value={values.address}
          data-testid={TEST_ID_ADDRESS}
        />
        <FieldErrors touched={touched.address} value={errors.address} />
      </label>
      <div className="flex flex-wrap justify-between mt3 mv3-ns">
        <div className="w-100 w-50-ns pr2-ns mv3 mv0-ns">
          <label className={`${styles.heading3} ttn`}>
            <FormattedMessage id="forms.address.zip" />
            <input
              {...commonInputProps}
              type="text"
              name="zip"
              autoComplete="postal-code"
              placeholder={formatMessage({ id: "forms.address.zip" })}
              onChange={handleChange}
              value={values.zip}
              data-testid={TEST_ID_POSTAL_CODE}
            />
            <FieldErrors touched={touched.zip} value={errors.zip} />
          </label>
        </div>
        <div className="w-100 w-50-ns pl2-ns mv3 mv0-ns">
          <label className={`${styles.heading3} ttn`}>
            <FormattedMessage id="forms.address.city" />
            <input
              {...commonInputProps}
              type="text"
              name="city"
              autoComplete="address-level2"
              placeholder={formatMessage({ id: "forms.address.city" })}
              onChange={handleChange}
              value={values.city}
              data-testid={TEST_ID_CITY}
            />
            <FieldErrors touched={touched.city} value={errors.city} />
          </label>
        </div>
      </div>
      <label className={`${styles.heading3} ttn mv3`}>
        <FormattedMessage id="forms.user.email" />
        <input
          {...commonInputProps}
          name="email"
          type="email"
          autoComplete="email"
          placeholder={formatMessage({ id: "forms.user.email" })}
          value={values.email}
          onChange={handleChange}
          data-testid={TEST_ID_EMAIL}
        />
        <FieldErrors touched={touched.email} value={errors.email} />
      </label>
      <label className={`${styles.heading3} ttn mv3`}>
        <FormattedMessage id="forms.user.phone" />
        <input
          {...commonInputProps}
          type="text"
          name="phone"
          autoComplete="tel"
          placeholder={formatMessage({ id: "forms.user.phone" })}
          onChange={handleChange}
          value={values.phone}
          data-testid={TEST_ID_PHONE}
        />
        <FieldErrors touched={touched.phone} value={errors.phone} />
      </label>
      <SeasonPassPhotoUpload
        photo={photo}
        isLoading={isLoading}
        onChange={handlePhotoChange}
        onFocus={handleFocus}
        error={photoError}
      />
      <label className="mt2 mb4 checkbox-wrapper">
        <input
          type="checkbox"
          name="termsAccepted"
          checked={values.termsAccepted}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          data-testid={TEST_ID_TERMS_ACCEPTED}
        />
        <span className="flex items-center">
          <FormattedMessage
            id="seasonpass.termsAccepted"
            values={{
              termsLink: (
                <LocaleLink
                  slug={
                    locale === "fi" ? "sopimusehdot" : "terms-and-conditions"
                  }
                >
                  <a
                    className="green no-underline underline-hover pointer ml1"
                    target="_blank"
                    data-testid="seasonpass-terms-link"
                  >
                    <FormattedMessage id="seasonpass.termsLink" />
                  </a>
                </LocaleLink>
              ),
            }}
          />
        </span>
        <FieldErrors
          touched={touched.termsAccepted}
          value={errors.termsAccepted}
        />
      </label>
      {submitError && (
        <ErrorContainer message={formatMessage({ id: submitError })} />
      )}
      <div className="mt4 flex flex-wrap-reverse justify-between">
        <div className="w-100 w-50-ns pr1-ns">
          <button
            type="button"
            onClick={onClose}
            className={`${styles.primaryButton} bg-transparent w-100`}
            data-testid={TEST_ID_CANCEL_BUTTON}
          >
            <FormattedMessage id="site.cancel" />
          </button>
        </div>
        <div className="w-100 w-50-ns pb2 pb0-ns pl1-ns">
          <button
            type="submit"
            disabled={isLoading}
            className={`${styles.secondaryButton} w-100`}
            data-testid={TEST_ID_SUBMIT_BUTTON}
          >
            {isEditing ? (
              <FormattedMessage id="seasonpass.updatePass" />
            ) : (
              <FormattedMessage id="product.addToCart" />
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SeasonPassProductModal;
